import { canShowRecentChats, ChatListItem } from '@lib/network/chats';
import { CircularProgress, Typography } from '@mui/material';
import { chatListStore } from '@stores/ChatList';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CHAT_GROUP_GAP, CHAT_HEADER_HEIGHT, CHAT_ROW_HEIGHT } from './chatListMetrics';
import { ChatMenuItem } from './ChatMenuItem';
import { ChatScrollList } from './ChatScrollList';
import { ChatSectionHeader } from './ChatSectionHeader';
import css from './RecentChatsSection.module.scss';

interface Props {
  /** When true, render the section as `display: none` so it occupies
   *  no layout space (drawer is collapsed). Kept mounted so its
   *  initial fetch runs once on app load and data is already there
   *  on first expand — no mount-on-expand layout shift. */
  hidden?: boolean;
  /** Called when a chat row is tapped. The mobile overlay passes its
   *  close handler here (like AccountFooter's onNavigate) — opening a
   *  chat is async and fallible, so the overlay must not stay open
   *  waiting for a page unload that may never come. */
  onNavigate?: () => void;
}

// The Starred and Recents groups (their headers included) share ONE virtualized
// scroll container and scroll together, so there's a single scrollbar.
type SectionKey = 'starred' | 'recents';

type ChatListRow =
  | { type: 'header'; label: string; section: SectionKey; marginTop?: number }
  | { type: 'chat'; chat: ChatListItem };

// Per-group collapsed state, persisted so a collapsed group stays collapsed across
// reloads. Stored under one key as `{ starred, recents }`; malformed / unavailable
// storage falls back to "everything expanded".
const COLLAPSE_STORAGE_KEY = 'nav.chatSections.collapsed';

function readCollapsed(): Record<SectionKey, boolean> {
  try {
    const parsed = JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) ?? '{}');
    return { starred: !!parsed?.starred, recents: !!parsed?.recents };
  } catch {
    return { starred: false, recents: false };
  }
}

function persistCollapsed(next: Record<SectionKey, boolean>): Record<SectionKey, boolean> {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore unavailable storage — collapse state just won't persist
  }
  return next;
}

// Row heights live in `chatListMetrics` (shared with the `chat-item` theme variant).
// The `.header` / `chat-item` elements fill these slots exactly, so the virtualizer's
// estimates are treated as exact (no dynamic measurement).

/**
 * Renders the chat history under the main navbar items: the "Starred" group (shown
 * only when ≥1 chat is starred) and the "Recents" group share a single virtualized
 * scroll container and scroll together. Reads from `chatListStore`. Returns `null`
 * unless both `CHAT_API_URL` and `CHAT_URL` are configured at build time (see
 * `canShowRecentChats`); otherwise stays mounted (CSS-hidden when the drawer is
 * collapsed) so the fetch runs once at app start.
 */
export const RecentChatsSection = observer(function RecentChatsSection({
  hidden,
  onNavigate,
}: Props) {
  // Wires up token reactions + visibilitychange refetch. Lives here
  // (not in the store's constructor) to dodge a TDZ from a circular
  // import at module-load time. The effect cleanup tears down the
  // listener so HMR / test re-mounts don't stack `visibilitychange`
  // handlers on `document`. Guarded on `canShowRecentChats()` so the
  // store doesn't register listeners on builds that never show the
  // section.
  useEffect(() => {
    if (!canShowRecentChats()) return;
    return chatListStore.start();
  }, []);

  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>(readCollapsed);

  const toggleSection = useCallback((section: SectionKey) => {
    setCollapsed((prev) => persistCollapsed({ ...prev, [section]: !prev[section] }));
  }, []);

  // Ensure a group is expanded for the current session when a chat moves into it
  // (star / unstar), so the moved row doesn't vanish behind a collapsed header.
  // Deliberately does NOT persist — unlike `toggleSection` — so an auto-expand can't
  // silently overwrite the user's saved collapse preference; a reload restores
  // whatever they last chose. No-op (no re-render) when already expanded.
  const expandSection = useCallback((section: SectionKey) => {
    setCollapsed((prev) => (prev[section] ? { ...prev, [section]: false } : prev));
  }, []);

  // Stable across renders so ChatMenuItem's observer() memo can skip unchanged rows
  // during scroll — an inline closure here would defeat that bail-out.
  const handleStarToggle = useCallback(
    (willBeStarred: boolean) => expandSection(willBeStarred ? 'starred' : 'recents'),
    [expandSection],
  );

  const { starred, recent, fetching, initialized } = chatListStore;
  const isEmpty = starred.length === 0 && recent.length === 0;

  // Combined [Starred header, ...starred, Recents header, ...recents] list — one
  // virtualized scroll region, so the whole history scrolls under one scrollbar. A
  // collapsed group keeps its header row but drops its chat rows; the virtualizer
  // recomputes its total height from the shorter list. Memoized so a parent
  // re-render or the `hidden` flip doesn't rebuild the array (and hand the
  // virtualizer new item refs) when the chats and collapse state are unchanged.
  const rows: ChatListRow[] = useMemo(() => {
    const sections: { key: SectionKey; label: string; chats: ChatListItem[] }[] = [
      { key: 'starred', label: 'Starred', chats: starred },
      { key: 'recents', label: 'Recents', chats: recent },
    ];
    const next: ChatListRow[] = [];
    for (const { key, label, chats } of sections) {
      if (chats.length === 0) continue;
      next.push({
        type: 'header',
        label,
        section: key,
        // A following group's header gets extra space above it; the first one
        // sits flush under the nav's own `.topNavButtons` gap.
        marginTop: next.length > 0 ? CHAT_GROUP_GAP : undefined,
      });
      if (!collapsed[key]) chats.forEach((chat) => next.push({ type: 'chat', chat }));
    }
    return next;
  }, [starred, recent, collapsed]);

  const getKey = useCallback(
    (row: ChatListRow) => (row.type === 'header' ? `header-${row.label}` : row.chat._id),
    [],
  );

  const estimateSize = useCallback(
    (index: number) => {
      const row = rows[index];
      return row.type === 'header' ? CHAT_HEADER_HEIGHT + (row.marginTop ?? 0) : CHAT_ROW_HEIGHT;
    },
    [rows],
  );

  const renderItem = useCallback(
    (row: ChatListRow) =>
      row.type === 'header' ? (
        <ChatSectionHeader
          label={row.label}
          collapsed={collapsed[row.section]}
          onToggle={() => toggleSection(row.section)}
        />
      ) : (
        <ChatMenuItem chat={row.chat} onStarToggle={handleStarToggle} onNavigate={onNavigate} />
      ),
    [collapsed, toggleSection, handleStarToggle, onNavigate],
  );

  if (!canShowRecentChats()) return null;

  return (
    <div className={classNames(css.section, hidden && css.hidden)}>
      {fetching && !initialized && (
        <div className={css.loading}>
          <CircularProgress size={20} />
        </div>
      )}

      {rows.length > 0 && (
        <ChatScrollList
          items={rows}
          getKey={getKey}
          estimateSize={estimateSize}
          renderItem={renderItem}
        />
      )}

      {initialized && isEmpty && (
        <Typography variant="caption" className={css.empty}>
          No chats yet
        </Typography>
      )}
    </div>
  );
});
